import {
  IN_APP_BROWSER_UA_PATTERN,
  FACEBOOK_IN_APP_UA_PATTERN,
  FACEBOOK_TRACKING_PARAMS,
  MESSENGER_IN_APP_UA_PATTERN,
  IN_APP_BROWSER_APP_NAMES,
  EXTERNAL_BROWSER_HANDOFF_TIMEOUT_MS,
} from '../constants/bookingConst';

/**
 * Reads the user agent, defaulting to the current navigator's.
 *
 * @param {string} [userAgent]
 * @returns {string} the UA string, or '' outside a browser
 */
const resolveUserAgent = (userAgent) => (
  userAgent ?? (typeof navigator !== 'undefined' ? navigator.userAgent || '' : '')
);

/**
 * Detects whether the page is running inside a social-media in-app browser
 * (Facebook, Messenger, Instagram, TikTok, etc.).
 *
 * @param {string} [userAgent] - defaults to the current navigator's UA
 * @returns {boolean} true when the UA matches a known in-app webview
 */
export function isInAppBrowser(userAgent) {
  return IN_APP_BROWSER_UA_PATTERN.test(resolveUserAgent(userAgent));
}

/**
 * Detects Facebook's own in-app browser (Facebook app or Messenger).
 *
 * @param {string} [userAgent] - defaults to the current navigator's UA
 * @returns {boolean} true inside Facebook's webview
 */
export function isFacebookInAppBrowser(userAgent) {
  return FACEBOOK_IN_APP_UA_PATTERN.test(resolveUserAgent(userAgent));
}

/**
 * Names the Meta app whose browser the page is in, for the manual-steps
 * guide's picture of that app's top bar.
 *
 * @param {string} [userAgent] - defaults to the current navigator's UA
 * @returns {string} 'Messenger' or 'Facebook'
 */
export function getInAppBrowserAppName(userAgent) {
  return MESSENGER_IN_APP_UA_PATTERN.test(resolveUserAgent(userAgent))
    ? IN_APP_BROWSER_APP_NAMES.MESSENGER
    : IN_APP_BROWSER_APP_NAMES.FACEBOOK;
}

/**
 * Removes Facebook's tracking parameters (fbclid, ...) from a URL, keeping
 * the path and any of the site's own parameters (e.g. ?book=1) intact.
 *
 * @param {string} url - absolute http(s) URL
 * @returns {string} the cleaned URL, or the input unchanged if unparseable
 */
export function stripTrackingParams(url) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  FACEBOOK_TRACKING_PARAMS.forEach((param) => parsed.searchParams.delete(param));
  return parsed.toString();
}

/**
 * Detects the device family that decides how a page is handed to the
 * browser.
 *
 * iPads count as iOS even when they report a Mac user agent (iPadOS asks for
 * desktop sites by default): a touch-capable "Macintosh", or Facebook's own
 * FBIOS marker, is an iPad, not a Mac.
 *
 * @param {string} [userAgent] - defaults to the current navigator's UA
 * @returns {'android'|'ios'|'other'}
 */
export function getDevicePlatform(userAgent) {
  const ua = resolveUserAgent(userAgent);
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod|FBIOS/i.test(ua)) return 'ios';
  const hasTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1;
  if (/macintosh/i.test(ua) && hasTouch) return 'ios';
  return 'other';
}

/**
 * Builds a URL that asks the OS to reopen the given page in the device's
 * default browser instead of the in-app webview.
 *
 * Why platform-specific schemes: in-app webviews ignore target="_blank", so a
 * plain link just reloads inside the same webview.
 *  - Android: an intent:// URL hands the page to the default browser; the
 *    S.browser_fallback_url keeps it working if no handler is found.
 *  - iOS: x-safari-https:// opens the page in Safari (iOS 17+; the in-app
 *    browser may ask the guest to confirm first).
 *  - Anything else: the plain URL, opened as a new window by the caller.
 *
 * @param {string} url - absolute http(s) URL to open
 * @param {string} [userAgent] - defaults to the current navigator's UA
 * @returns {string} the URL to navigate to
 */
export function buildExternalBrowserUrl(url, userAgent) {
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return url;
  }
  const scheme = parsed.protocol.replace(':', '');
  const rest = `${parsed.host}${parsed.pathname}${parsed.search}${parsed.hash}`;
  const platform = getDevicePlatform(userAgent);

  if (platform === 'android') {
    return `intent://${rest}#Intent;scheme=${scheme};action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(url)};end`;
  }
  if (platform === 'ios') {
    return `x-safari-${scheme}://${rest}`;
  }
  return url;
}

/**
 * Works out where the current page should be sent to reach the device's
 * external browser.
 *
 * @returns {{cleanUrl: string, externalUrl: string, hasScheme: boolean}}
 *   cleanUrl is the page without Facebook's tracking params; externalUrl is
 *   the platform hand-off URL (intent:// or x-safari-https://), or cleanUrl
 *   itself when the device has none (hasScheme false).
 */
export function getExternalBrowserTarget() {
  if (typeof window === 'undefined') return { cleanUrl: '', externalUrl: '', hasScheme: false };
  const cleanUrl = stripTrackingParams(window.location.href);
  const externalUrl = buildExternalBrowserUrl(cleanUrl);
  return { cleanUrl, externalUrl, hasScheme: externalUrl !== cleanUrl };
}

/**
 * Sends the current page to the device's external browser, on the site's
 * normal URL (Facebook's fbclid etc. removed).
 *
 * It never reloads the page to its plain URL: inside the webview that only
 * reloads where the guest already is, and on iOS it cancelled Facebook's
 * "Open in Safari?" prompt. Pair it with watchExternalBrowserHandoff() to
 * find out when the hand-off did not happen.
 *
 * @returns {void}
 */
export function openInExternalBrowser() {
  const { cleanUrl, externalUrl, hasScheme } = getExternalBrowserTarget();
  if (!cleanUrl) return;

  if (!hasScheme) {
    // No OS scheme for this device. A new window is the request some in-app
    // browsers pass to the system browser; assigning location would only
    // reload the page where it already is.
    window.open(cleanUrl, '_blank', 'noopener');
    return;
  }
  window.location.href = externalUrl;
}

/**
 * Watches for the hand-off to the external browser after a tap, and calls
 * `onFailed` if it did not happen.
 *
 * "Did not happen" means the page is still in front once
 * EXTERNAL_BROWSER_HANDOFF_TIMEOUT_MS has passed. Any sign the hand-off is
 * under way — the page losing focus to the browser's own "Open in…?"
 * prompt, being hidden, or being left — counts as success and stops the
 * watch, so a prompt that is already showing is never covered or
 * interrupted. Call it just before openInExternalBrowser().
 *
 * @param {() => void} onFailed - run when the page is still in front
 * @param {number} [delayMs] - how long to give the hand-off
 * @returns {() => void} stops the watch
 */
export function watchExternalBrowserHandoff(onFailed, delayMs = EXTERNAL_BROWSER_HANDOFF_TIMEOUT_MS) {
  if (typeof window === 'undefined') return () => {};

  let timer = null;
  const handleVisibility = () => {
    if (document.visibilityState === 'hidden') cancel();
  };
  const cancel = () => {
    window.clearTimeout(timer);
    window.removeEventListener('blur', cancel);
    window.removeEventListener('pagehide', cancel);
    document.removeEventListener('visibilitychange', handleVisibility);
  };

  window.addEventListener('blur', cancel);
  window.addEventListener('pagehide', cancel);
  document.addEventListener('visibilitychange', handleVisibility);
  timer = window.setTimeout(() => {
    cancel();
    onFailed();
  }, delayMs);

  return cancel;
}
